import { useChat } from "../hooks/useChat";
import { useState, useEffect, useRef } from "react";
import { useTheme } from "../context/ThemeContext";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import API_BASE_URL from "../config";

export default function ChatBox() {
    const messagesContainerRef = useRef(null);
    const { messages, sendMessage, loadMore, take } = useChat(messagesContainerRef);

    const [text, setText] = useState("");
    const [justSent, setJustSent] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(true);
    const [lastMessageId, setLastMessageId] = useState(null);
    const [lastSeenMessageId, setLastSeenMessageId] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const notificationSound = useRef(new Audio("/sounds/messenger.mp3")).current;
    const [fileToSend, setFileToSend] = useState(null);

    const textareaRef = useRef(null);
    const { darkMode } = useTheme();

    const currentUserId = getCurrentUserId();


    const isEmojiOnly = (text) => {
        const trimmed = text.trim();
        if (!trimmed) return false;

        // kell legyen benne legalább egy emoji
        const hasEmoji = /\p{Emoji}/u.test(trimmed);

        // NEM tartalmazhat betűt, számot vagy sima írásjelet
        const hasInvalid = /[\p{L}\p{N}\p{P}]/u.test(trimmed);

        return hasEmoji && !hasInvalid;
    };

    function getCurrentUserId() {
        const stored = localStorage.getItem("userId");
        if (stored) return Number(stored);

        const token = localStorage.getItem("token");
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split(".")[1]));
                return Number(payload.sub); // vagy payload.id ha úgy van az API-ban
            } catch {
                return 0;
            }
        }
        return 0;
    }

    const handleSend = async () => {
        if (!text.trim() && !fileToSend) return;

        const token = localStorage.getItem("token");

        // 🔹 ha van fájl, feltöltés az Azure-ra
        if (fileToSend) {
            const formData = new FormData();
            formData.append("file", fileToSend);
            formData.append("senderId", currentUserId);

            try {
                const res = await fetch(`${API_BASE_URL}/api/chat/upload`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                });
                const data = await res.json();
                if (!data.success) console.error("❌ Upload failed:", data);
            } catch (err) {
                console.error("❌ Upload error:", err);
            }

            setFileToSend(null); // feltöltés után töröljük
        }

        // 🔹 ha van szöveg, SignalR-en küldjük
        if (text.trim()) {
            sendMessage(text);
            setText("");
        }

        setJustSent(true);
        if (textareaRef.current) {
            textareaRef.current.style.height = "40px";
            textareaRef.current.style.overflowY = "hidden";
        }
    };


    const handleLike = () => {
        sendMessage("👍");
    };

    const handleEmojiSelect = (emoji) => {
        setText((prev) => prev + emoji.native);
        setShowEmojiPicker(false);
        textareaRef.current?.focus();
    };

    // Új üzenet figyelése
    useEffect(() => {
        if (messages.length > 0) {
            const last = messages[messages.length - 1];
            if (lastMessageId === null) {
                setLastMessageId(last.id);
                setLastSeenMessageId(last.id);
                return;
            }
            if (last.id !== lastMessageId) {
                setLastMessageId(last.id);

                if (last.senderId !== currentUserId) {
                    // hang lejátszás
                    notificationSound.play().catch(() => {
                        console.warn("Autoplay blokkolva, user interakció után fog szólni.");
                    });

                    if (!isOpen) {
                        setUnreadCount((prev) => prev + 1);
                    }
                }
            }

        }
    }, [messages, lastMessageId, isOpen, currentUserId]);

    // ha kinyitod a chatet → ugrás az utoljára látott üzenetre
    useEffect(() => {
        if (isOpen && lastSeenMessageId && messagesContainerRef.current) {
            const el = messagesContainerRef.current.querySelector(
                `[data-msgid="${lastSeenMessageId}"]`
            );
            if (el) el.scrollIntoView({ behavior: "auto", block: "center" });
            setUnreadCount(0);
        }
    }, [isOpen, lastSeenMessageId]);

    const handleScroll = (e) => {
        const container = e.target;
        const bottom =
            container.scrollHeight - container.scrollTop - container.clientHeight < 5;
        if (bottom) setUnreadCount(0);

        if (messages.length > 0) {
            const lastVisible = messages[messages.length - 1];
            setLastSeenMessageId(lastVisible.id);
        }
    };

    // ha TE küldtél → scroll aljára
    useEffect(() => {
        if (justSent && messagesContainerRef.current) {
            setTimeout(() => {
                messagesContainerRef.current.scrollTop =
                    messagesContainerRef.current.scrollHeight;
            }, 50);
            setJustSent(false);
        }
    }, [messages, justSent]);

    // ha zárva van → ikon mutassa az unreadCount-ot
    if (!isOpen) {
        return (
            <div className="fixed bottom-4 right-4">
                <button
                    onClick={() => {
                        setIsOpen(true);
                        setUnreadCount(0);
                    }}
                    className="relative bg-red-600 text-white px-4 py-2 rounded-full shadow-lg text-sm font-semibold"
                >
                    💬 Chat
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-yellow-300 text-black text-xs w-5 h-5 flex items-center justify-center rounded-full">
                            {unreadCount}
                        </span>
                    )}
                </button>
            </div>
        );
    }

    return (
        <div
            className={`fixed bottom-4 right-4 w-[28rem] h-[60vh] border rounded-lg shadow-2xl flex flex-col z-[10000] ${darkMode ? "bg-gray-900 text-white" : "bg-white text-black"
                }`}
        >
            {/* Fejléc */}
            <div
                className="bg-red-600 text-white px-3 py-2 rounded-t-lg text-sm font-bold flex justify-between items-center cursor-pointer"
                onClick={() => setIsOpen(false)}
            >
                <span className="flex items-center gap-2">
                    💬 Chat
                    {unreadCount > 0 && (
                        <span className="bg-yellow-300 text-black text-xs px-2 py-0.5 rounded-full">
                            {unreadCount}
                        </span>
                    )}
                </span>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(false);
                    }}
                    className="text-white hover:text-gray-200 font-bold"
                >
                    ✕
                </button>
            </div>

            {/* Üzenetek */}
            <div
                ref={messagesContainerRef}
                className={`flex-1 overflow-y-auto p-3 space-y-3 ${darkMode ? "bg-gray-800" : "bg-gray-50"
                    }`}
                onScroll={handleScroll}
            >
                {messages.length >= take && (
                    <button
                        onClick={loadMore}
                        className="mx-auto mb-4 px-4 py-2 text-sm font-semibold 
                        rounded-full shadow-md transition-all duration-200
                        bg-gradient-to-r from-red-500 to-orange-500 text-white
                        hover:from-red-600 hover:to-orange-600 active:scale-95"
                    >
                        ⬆️ Încarcă mai multe mesaje
                    </button>
                )}

                {messages.length === 0 ? (
                    <p className="text-gray-400 text-center text-sm">
                        Nu există mesaje...
                    </p>
                ) : (
                    messages.map((m) => {
                        const isMe = m.senderId === currentUserId;
                        const utcDate = new Date(
                            m.createdUtc.endsWith("Z") ? m.createdUtc : m.createdUtc + "Z"
                        );
                        const time = utcDate.toLocaleTimeString("ro-RO", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                            timeZone: "Europe/Bucharest",
                        });

                        return (
                            <div
                                key={m.id}
                                data-msgid={m.id}
                                className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                            >
                                {/* Üzenet kártya */}
                                <div
                                    className={`px-4 py-2 rounded-2xl w-full max-w-[100%] text-base shadow ${isMe
                                        ? darkMode
                                            ? "bg-gray-700 text-gray-100 rounded-br-none"
                                            : "bg-blue-100 text-gray-900 rounded-br-none"
                                        : darkMode
                                            ? "bg-gray-700 text-gray-100 rounded-bl-none"
                                            : "bg-blue-100 text-gray-900 rounded-bl-none"

                                        }`}
                                >
                                    <div
                                        className={`text-sm font-bold mb-1 ${darkMode ? "text-blue-300" : "text-blue-600"
                                            }`}
                                    >
                                        {m.senderName}
                                    </div>
                                    <div className="leading-snug break-words whitespace-pre-line w-full">
                                        {m.text && <div>{m.text}</div>}

                                        {m.fileUrl && (
                                            <div className="mt-2">
                                                {m.fileType?.startsWith("image/") ? (
                                                    <img
                                                        src={m.fileUrl.startsWith("http") ? m.fileUrl : API_BASE_URL + m.fileUrl}
                                                        alt={m.fileName}
                                                        className="max-w-xs rounded shadow border"
                                                    />

                                                ) : (
                                                    <a
                                                        href={m.fileUrl.startsWith("http") ? m.fileUrl : API_BASE_URL + m.fileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-2 text-yellow-500 hover:text-yellow-600 font-semibold break-all"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="w-5 h-5 flex-shrink-0 fill-current"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6zM13 9V3.5L18.5 9H13z" />
                                                        </svg>
                                                        <span className="truncate max-w-[200px]">{m.fileName || "Document"}</span>
                                                    </a>

                                                )}
                                            </div>
                                        )}
                                    </div>


                                    <div
                                        className={`text-sm mt-2 text-right italic tracking-wide ${isMe
                                            ? darkMode
                                                ? "text-gray-300"
                                                : "text-gray-700"
                                            : darkMode
                                                ? "text-gray-300"
                                                : "text-gray-700"
                                            }`}
                                    >
                                        {time}
                                    </div>

                                </div>

                            </div>
                        );
                    })
                )}

                {showEmojiPicker && (
                    <div className="absolute bottom-20 right-20 z-50">
                        <Picker
                            data={data}
                            onEmojiSelect={handleEmojiSelect}
                            theme={darkMode ? "dark" : "light"}
                        />
                    </div>
                )}
            </div>

            {/* Input */}
            <div
                className={`relative flex items-center gap-2 border-t p-2 rounded-b-lg ${darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
                    }`}
            >
                {/* Textarea konténer */}
                <div className="relative flex-1">
                    {fileToSend && (
                        <div
                            className={`flex items-center gap-2 mb-2 p-2 rounded-md border text-sm transition-colors ${darkMode
                                ? "bg-gray-800 border-gray-700 text-gray-200"
                                : "bg-yellow-50 border-yellow-200 text-gray-800"
                                }`}
                        >
                            {fileToSend.type.startsWith("image/") ? (
                                <img
                                    src={URL.createObjectURL(fileToSend)}
                                    alt="preview"
                                    className="w-12 h-12 object-cover rounded border border-gray-500/30"
                                />
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`w-5 h-5 ${darkMode ? "text-yellow-400" : "text-yellow-600"
                                        } flex-shrink-0`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M7 21h10a2 2 0 0 0 2-2V9l-6-6H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z"
                                    />
                                </svg>
                            )}

                            <span className="truncate max-w-[200px]">{fileToSend.name}</span>

                            <button
                                onClick={() => setFileToSend(null)}
                                className={`ml-auto font-bold text-xs transition ${darkMode
                                    ? "text-red-400 hover:text-red-300"
                                    : "text-red-500 hover:text-red-700"
                                    }`}
                            >
                                ✕
                            </button>
                        </div>
                    )}


                    <textarea
                        ref={textareaRef}
                        value={text}
                        onChange={(e) => {
                            setText(e.target.value);

                            // csak akkor engedjük növekedni, ha több sor van
                            if (e.target.value.includes("\n") || e.target.scrollHeight > 40) {
                                e.target.style.height = "auto";
                                const newHeight = Math.min(e.target.scrollHeight, 240);
                                e.target.style.height = newHeight + "px";
                                e.target.style.overflowY = newHeight >= 240 ? "auto" : "hidden";
                            } else {
                                // ha csak 1 sor van → fix magasság
                                e.target.style.height = "40px";
                                e.target.style.overflowY = "hidden";
                            }
                        }}

                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder="Scrieți un mesaj..."
                        className={`flex-1 px-3 py-2 pr-24 focus:outline-none focus:ring-2 focus:ring-red-500 
              text-base placeholder:text-base resize-none ${darkMode
                                ? "bg-gray-800 text-white border border-gray-600 rounded"
                                : "bg-white text-gray-900 border border-gray-300 rounded"
                            }`}
                        style={{
                            height: "40px",
                            minHeight: "40px",
                            width: "370px",
                            maxHeight: "240px",
                            lineHeight: "1.5em",
                            overflowY: "hidden",
                        }}
                    />
                    {/* 📎 File upload input (láthatatlan) */}
                    <input
                        type="file"
                        id="chatFileInput"
                        hidden
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setFileToSend(file);
                            e.target.value = ""; // reset, hogy újra lehessen választani ugyanazt
                        }}
                    />


                    <button
                        type="button"
                        onClick={() => document.getElementById("chatFileInput").click()}
                        className="absolute right-32 bottom-2.5 w-9 h-9 flex items-center justify-center rounded-full 
             hover:bg-gray-200 dark:hover:bg-gray-700 transition active:scale-95"
                        title="Trimite fișier"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={`w-5 h-5 ${darkMode ? "text-yellow-400" : "text-gray-600"} hover:scale-110 transition`}
                        >
                            <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3 3 0 0 1 4.24 4.24L9.88 16.13" />
                        </svg>
                    </button>


                    {/* Emoji gomb (fix jobb alsó sarokban) */}
                    <button
                        type="button"
                        onClick={() => setShowEmojiPicker((prev) => !prev)}
                        className="absolute right-20 bottom-2.5 w-8 h-8 flex items-center justify-center text-2xl
             focus:outline-none focus:ring-0 bg-transparent hover:bg-transparent active:bg-transparent"
                    >
                        😊
                    </button>

                    {/* Like / Küldés gomb (fix jobb alsó sarokban) */}
                    {/* Like / Küldés gomb (fix jobb alsó sarokban) */}
                    {!text.trim() && !fileToSend ? (
                        <button
                            onClick={handleLike}
                            className="absolute right-2 bottom-2 w-9 h-9 flex items-center justify-center rounded-full text-2xl"
                        >
                            👍
                        </button>
                    ) : (
                        <button
                            onClick={handleSend}
                            className={`absolute right-2 bottom-2 w-9 h-9 flex items-center justify-center rounded-full 
${darkMode ? "bg-red-500 hover:bg-red-600" : "bg-red-600 hover:bg-red-700"} 
text-white shadow active:scale-95`}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                                className="w-5 h-5"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6 12L3 21l18-9L3 3l3 9zm0 0h7"
                                />
                            </svg>
                        </button>
                    )}

                </div>
            </div>

        </div>
    );
}
